<!-- 用户通知 -->
<script lang="ts">
import Icon from '@/components/Icon/index.vue'
import { watch } from 'vue'
import { includes } from 'lodash-es'
import type { GetPopUpNoticeShowResponse, AttachFile } from '@/api/notice-config/model.d'
import { getPopUpNotice, markNoticeRead } from '@/api/notice-config'
import { goto } from '@/utils'
import { useUserStore } from '@/store/modules/user'
import { BUSINESS_TYPE } from '@/common/constants'
import { isNewbieGuideFinished } from '@/utils/guide'
import useNotification from '../js/useNotification'

export default {
  name: 'UserNotification'
}
</script>
<script setup lang="ts">
const notification = useNotification()
const count = computed(() => notification.count.value)
const userStore = useUserStore()

const userInfo = userStore.getUserInfo

function onClick() {
  goto('UserNotice')
  notification.refresh() // 刷新数量
}

/** 是否展示公告入口上的公告条数 */
const hideNotificationEntrance = computed(() => count.value === 0)

/** 是否展示公告入口 用户拥有摇钱树的业务 */
const showNotificationEntranceButton = computed(() =>
  includes(userInfo?.businessTypeList, BUSINESS_TYPE.CASH_COW)
)

const popup = reactive({
  show: false,
  detail: {} as GetPopUpNoticeShowResponse
})

/** 加载弹窗公告 */
async function loadPopupNotice() {
  try {
    const data = await getPopUpNotice()
    if (data) {
      popup.detail = data
      popup.show = true
      countDown()
    }
  } catch (error) {
    console.log(error)
  }
}
function onClose() {
  popup.show = false
  notification.refresh()
}
function onView() {
  const { id } = popup.detail
  goto('NoticeView', { id })
  popup.show = false
}

/** 附件列表，用于展示 */
const fileList = computed(() => {
  const { fileList } = popup.detail
  if (!fileList) return []
  return fileList.map((item) => {
    const { fileName, fileUrl } = item
    const uid = item.id as number
    return { uid, name: fileName, url: fileUrl }
  })
})

function jumpToFile(file: AttachFile) {
  const dom = document.querySelector('#notice-view-attachment')
  if (!dom) return
  dom.scrollIntoView({ behavior: 'smooth' })
  // 边框高亮
  const clsname = 'attachment-highlight'
  dom.classList.add(clsname)
  setTimeout(() => {
    let blur: Nullable<() => void> = () => {
      dom.classList.remove(clsname)
      blur && window.removeEventListener('click', blur)
      blur = null
    }
    // 取消高亮
    window.addEventListener('click', blur)
  }, 0)
}

const countDownSeconds = ref(5)
const countDown = () => {
  const timerId = setInterval(start, 1000)
  function start() {
    if (countDownSeconds.value === 0) {
      clearInterval(timerId)
    } else {
      countDownSeconds.value--
      if (countDownSeconds.value === 0) {
        markNoticeRead(popup.detail.id)
      }
    }
  }
}

watch(isNewbieGuideFinished, (val, oldVal) => {
  if (val && !oldVal) {
    /** 新手指引完成，则加载弹窗公告 */
    loadPopupNotice()
  }
})

onMounted(() => {
  /** 新手指引未完成，则不加载弹窗公告 */
  if (!isNewbieGuideFinished.value) return
  loadPopupNotice()
})
</script>
<template>
  <ElBadge
    v-if="showNotificationEntranceButton"
    :value="count"
    :max="999"
    :hidden="hideNotificationEntrance"
    class="notification leading-none"
    @click="onClick"
  >
    <ElButton link type="primary" class="p-0 leading-none h-auto text-strong">
      <Icon class="shrink-0 text-[#282E38] hover:text-main" icon="bell|svg" :size="22" />
    </ElButton>
  </ElBadge>
  <ElDialog
    v-model="popup.show"
    :width="980"
    top="5vh"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :append-to-body="true"
    :show-close="false"
    class="notification-dialog"
  >
    <div class="container">
      <div class="header">
        <div class="header-title" />
        <div class="header-icon" />
      </div>
      <div class="body">
        <div class="mb-4 font-bold text-lg text-[#262626]">{{ popup.detail.title }}</div>
        <div v-if="popup.detail.fileList.length" class="flex flex-wrap items-center mb-4">
          <span class="p-2 bg-[#F2F7FF] text-main leading-none"
            >{{ popup.detail.fileList.length }}个附件</span
          >
          <span
            v-for="(file, index) in popup.detail.fileList"
            :key="index"
            class="p-2 text-main flex items-center cursor-pointer hover:opacity-80"
            @click="jumpToFile(file)"
          >
            <Icon class="icon" icon="attach-file|svg" :size="14" />
            <span class="ml-1 leading-none">{{ file.fileName }}</span>
          </span>
        </div>
        <div class="border-solid border-b border-[#F2F3F5] border-t-0 border-l-0 border-r-0" />
        <div class="content py-6">
          <div v-html="popup.detail.content" />
        </div>
        <div
          v-if="popup.detail.fileList.length"
          id="notice-view-attachment"
          class="py-4 px-5 border border-solid rounded-md border-[#F2F3F5]"
        >
          <GUpload :file-list="fileList" action="" disabled />
        </div>
      </div>
      <div class="footer">
        <ElButton :disabled="countDownSeconds > 0" @click="onClose"
          >关闭<span v-if="countDownSeconds">（{{ countDownSeconds }}）</span>
        </ElButton>
        <ElButton type="primary" @click="onView">查看详情</ElButton>
      </div>
    </div>
  </ElDialog>
</template>
<style lang="scss" scoped>
.notification {
  :deep(.sp-badge__content) {
    transform: translateY(-40%) translateX(85%);
  }
}
</style>
<style lang="scss">
.notification-dialog.sp-dialog {
  border-radius: 6px;
  max-height: 88vh;

  .sp-dialog__body,
  .sp-dialog__header,
  .sp-dialog__footer {
    padding: 0;
  }

  .container {
    padding: 24px;
    background-image: url('./dialog-bg.svg');
    background-repeat: no-repeat;
    background-size: 100% auto;
    border-radius: 6px;
    position: relative;
    display: flex;
    flex-direction: column;
    max-height: 88vh;

    .header {
      display: flex;
      justify-content: space-between;
      &-title {
        height: 75px;
        flex: 1;
        background-image: url('./dialog-title.svg');
        background-repeat: no-repeat;
        background-size: contain;
        margin: 16px 0 0 24px;
        position: relative;
        z-index: 1;
      }
      &-icon {
        height: 122px;
        width: 146px;
        background-image: url('./dialog-laba.svg');
        background-repeat: no-repeat;
        background-size: contain;
        margin: -24px 48px 0 0;
        position: relative;
        z-index: 1;
      }
    }
    .body {
      background-color: #fff;
      padding: 24px;
      border-radius: 6px;
      margin-top: -12px;
      margin-bottom: 48px;
      position: relative;
      z-index: 2;
      overflow-y: auto;

      img {
        max-width: 100%;
        height: auto;
      }

      .sp-upload {
        display: none;
      }
      .sp-upload-list {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        align-items: center;
        .sp-upload-list__item {
          margin-right: 4px;
          width: 260px;
          overflow: hidden;
        }
        .g-file-delete {
          display: none;
        }
      }
    }
    .footer {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 64px;
      line-height: 64px;
      text-align: center;
      background-color: transparent;
      z-index: 3;
    }
  }
}
</style>
